import { useNavigate } from 'react-router-dom';
import { Button, Typography, Card, Chip } from '@mui/material';
import { AnalysisSummary } from '@/api/domains/analyses/types';
import { getChipColor } from '../../utils';
import { formatDate } from '@/utils/formatDate';
import styles from './AnalysisCard.module.css';
import { DocumentTitle } from '../DocumentTitle/DocumentTitle';

interface AnalysisCardProps {
  analysis: AnalysisSummary;
}

export const AnalysisCard = ({ analysis }: AnalysisCardProps) => {
  const navigate = useNavigate();

  return (
    <Card
      className={styles.analysisCard}
      onClick={() => navigate(`/analyses/${analysis.analysis_id}`)}
    >
      <div className={styles.cardHeader}>
        <Chip
          label={
            analysis.status.charAt(0) + analysis.status.slice(1).toLowerCase()
          }
          size="small"
          className={styles.statusChip}
          sx={{
            backgroundColor: getChipColor(analysis.status).background,
            color: getChipColor(analysis.status).color,
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.5px'
          }}
        />
        <Typography variant="caption" className={styles.dateText}>
          {analysis.completed_at
            ? `Completed ${formatDate(analysis.completed_at)}`
            : `Created ${formatDate(analysis.created_at)}`}
        </Typography>
      </div>

      <h3 className={styles.analysisName}>{analysis.analysis_name}</h3>

      <div className={styles.findingsRow}>
        <Chip
          label={`${analysis.findings_count} Findings`}
          size="small"
          className={styles.findingsChip}
        />
        {analysis.status === 'PROCESSING' && (
          <i
            className="pi pi-spin pi-spinner"
            style={{ fontSize: '0.9rem', marginLeft: 8 }}
            aria-hidden
          />
        )}
      </div>

      <div className={styles.documentsRow}>
        <div className={styles.documentItem}>
          <i className="pi pi-file" />
          <DocumentTitle
            documentId={analysis.document_1_id}
            fallback={analysis.document_1_id.split('/').pop()}
          />
        </div>
        <div className={styles.documentItem}>
          <i className="pi pi-file" />
          <DocumentTitle
            documentId={analysis.document_2_id}
            fallback={analysis.document_2_id.split('/').pop()}
          />
        </div>
      </div>

      <Button
        variant="outlined"
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/analyses/${analysis.analysis_id}`);
        }}
        className={styles.viewButton}
      >
        View Results
      </Button>
    </Card>
  );
};
